import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { isRTL, getAllSlugsForRoute } from "@/i18n";
import HomePage from "@/pages/HomePage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import InvestmentGuidePage from "@/pages/InvestmentGuidePage";
import AboutPage from "@/pages/AboutPage";
import InvestorRelationsPage from "@/pages/InvestorRelationsPage";
import ContactPage from "@/pages/ContactPage";
import BlogPage from "@/pages/BlogPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import AdminPage from "@/pages/AdminPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import InvestorDashboardPage from "@/pages/InvestorDashboardPage";
import InvestorPortfolioPage from "@/pages/InvestorPortfolioPage";
import InvestorTransactionsPage from "@/pages/InvestorTransactionsPage";
import InvestorDocumentsPage from "@/pages/InvestorDocumentsPage";
import InvestorFavoritesPage from "@/pages/InvestorFavoritesPage";
import InvestorProfilePage from "@/pages/InvestorProfilePage";
import StaffLayout from "@/components/StaffLayout";
import StaffDashboardPage from "@/pages/staff/StaffDashboardPage";
import StaffPropertiesPage from "@/pages/staff/StaffPropertiesPage";
import StaffPropertyFormPage from "@/pages/staff/StaffPropertyFormPage";
import StaffBlogPage from "@/pages/staff/StaffBlogPage";
import StaffBlogFormPage from "@/pages/staff/StaffBlogFormPage";
import StaffCustomersPage from "@/pages/staff/StaffCustomersPage";
import StaffCustomerFormPage from "@/pages/staff/StaffCustomerFormPage";
import StaffReportsPage from "@/pages/staff/StaffReportsPage";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminInvestmentsPage from "@/pages/admin/AdminInvestmentsPage";
import AdminDocumentsPage from "@/pages/admin/AdminDocumentsPage";
import AdminTransactionsPage from "@/pages/admin/AdminTransactionsPage";
import AdminActivitiesPage from "@/pages/admin/AdminActivitiesPage";

function LangLayout() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    const validLangs = ["tr", "en", "ar", "ru"];
    if (lang && validLangs.includes(lang)) {
      localStorage.setItem("oly_lang", lang);
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }
  }, [lang, i18n]);

  useEffect(() => {
    const rtl = isRTL(i18n.language);
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return <Outlet />;
}

/**
 * StaffRoute - Personel paneli için yetkilendirme kontrolü
 * Sadece staff veya admin rolüne sahip kullanıcılar erişebilir
 */
function StaffRoute({ children }) {
  const { user, isStaff, isAdmin, authReady } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authReady) return;

    if (!user) {
      navigate('/tr/giris', { replace: true });
    } else if (!isStaff && !isAdmin) {
      navigate('/tr', { replace: true });
    }
  }, [user, isStaff, isAdmin, authReady, navigate]);

  if (!authReady) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user || (!isStaff && !isAdmin)) {
    return null;
  }

  return children;
}

/**
 * AdminRoute - Admin paneli için yetkilendirme kontrolü
 * Sadece admin rolüne sahip kullanıcılar erişebilir
 */
function AdminRoute({ children }) {
  const { user, isAdmin, authReady } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authReady) return;

    if (!user) {
      navigate('/tr/giris', { replace: true });
    } else if (!isAdmin) {
      navigate('/tr', { replace: true });
    }
  }, [user, isAdmin, authReady, navigate]);

  if (!authReady) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return children;
}

function App() {
  return (
    <HelmetProvider>
      <CurrencyProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/:lang" element={<LangLayout />}>
                <Route index element={<HomePage />} />
                <Route path="properties/:id" element={<PropertyDetailPage />} />
                <Route path="invest" element={<InvestmentGuidePage />} />
                <Route path="blog" element={<BlogPage />} />
                <Route path="blog/:slug" element={<BlogDetailPage />} />
                {getAllSlugsForRoute("about").map((slug) => (
                  <Route key={slug} path={slug} element={<AboutPage />} />
                ))}
                <Route path="investor-relations" element={<InvestorRelationsPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="giris" element={<LoginPage />} />
                <Route path="kayit" element={<RegisterPage />} />
                <Route path="admin" element={<Navigate to="/admin" replace />} />
                <Route path="investor/dashboard" element={<InvestorDashboardPage />} />
                <Route path="investor/investments" element={<InvestorPortfolioPage />} />
                <Route path="investor/transactions" element={<InvestorTransactionsPage />} />
                <Route path="investor/documents" element={<InvestorDocumentsPage />} />
                <Route path="investor/favorites" element={<InvestorFavoritesPage />} />
                <Route path="investor/profile" element={<InvestorProfilePage />} />
              </Route>

              {/* Staff Panel Routes - Yetkilendirme Korumalı */}
              <Route path="/staff" element={<StaffRoute><StaffLayout /></StaffRoute>}>
                <Route index element={<StaffDashboardPage />} />
                <Route path="properties" element={<StaffPropertiesPage />} />
                <Route path="properties/new" element={<StaffPropertyFormPage />} />
                <Route path="properties/:id" element={<StaffPropertyFormPage />} />
                <Route path="blog" element={<StaffBlogPage />} />
                <Route path="blog/new" element={<StaffBlogFormPage />} />
                <Route path="blog/:id" element={<StaffBlogFormPage />} />
                <Route path="customers" element={<StaffCustomersPage />} />
                <Route path="customers/new" element={<StaffCustomerFormPage />} />
                <Route path="customers/:id" element={<StaffCustomerFormPage />} />
                <Route path="reports" element={<StaffReportsPage />} />
              </Route>

              {/* Admin Panel Routes - Sadece Admin Erişebilir */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="users/:id" element={<AdminUserDetailPage />} />
                <Route path="investments" element={<AdminInvestmentsPage />} />
                <Route path="documents" element={<AdminDocumentsPage />} />
                <Route path="transactions" element={<AdminTransactionsPage />} />
                <Route path="activities" element={<AdminActivitiesPage />} />
              </Route>

              <Route path="/" element={<Navigate to="/tr" replace />} />
              <Route path="*" element={<Navigate to="/tr" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </CurrencyProvider>
    </HelmetProvider>
  );
}

export default App;
